// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { ERROR_CODE_MAPPING, ERROR_MESSAGES } from '@/api/constants';
import { handleAPIError, hasUniqueViolationError } from '@/api/error-utils';
import { SupportSnackbar } from '@/components/support-snackbar';
import { createCustomer } from '@/customer/api/create-customer';
import { CreateCustomerFormFields } from '@/customer/components/create-customer-form-fields';
import {
  CreateCustomerFormData,
  DEFAULT_BILLING_VALUES,
  createCustomerFormResolver,
} from '@/customer/components/create-customer-form-schema';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SubmitHandler, useForm } from 'react-hook-form';

interface Props {
  defaultName?: string;
  defaultIsCustomer?: boolean;
  defaultIsSupplier?: boolean;
  mode?: 'sparse' | 'full';
  isOpen: boolean;
  onClose: () => void;
}

export function CreateCustomerDialog(props: Props) {
  const queryClient = useQueryClient();
  const mode = props.mode ?? 'sparse';

  const createCustomerMutation = useMutation({
    mutationFn: createCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer'] });
      close();
    },
    onError: (error) => {
      if (hasUniqueViolationError(error)) {
        setError('name', {
          type: 'custom',
          message: 'A company with this name already exists',
        });
        return;
      }

      if (
        handleAPIError(error).apiStatusCode ===
        ERROR_CODE_MAPPING.customer_invalid_billing_contact_email
      ) {
        setError('billingContact.email', {
          type: 'custom',
          message: ERROR_MESSAGES.customer_invalid_billing_contact_email,
        });
      }
    },
  });

  const {
    control,
    setError,
    reset,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateCustomerFormData>({
    resolver: createCustomerFormResolver,
    defaultValues: {
      name: props.defaultName ?? '',
      domain: null,
      isCustomer: props.defaultIsCustomer ?? true,
      isSupplier: props.defaultIsSupplier ?? false,
      ...DEFAULT_BILLING_VALUES,
    },
  });

  const isAlertOpen =
    createCustomerMutation.isError &&
    !hasUniqueViolationError(createCustomerMutation.error) &&
    handleAPIError(createCustomerMutation.error).apiStatusCode !==
      ERROR_CODE_MAPPING.customer_invalid_billing_contact_email &&
    !isPlanLimitError(createCustomerMutation.error);

  const close = () => {
    props.onClose();
    reset();
  };

  const handleValidSubmission: SubmitHandler<CreateCustomerFormData> = (
    data
  ) => {
    createCustomerMutation.mutate({
      name: data.name,
      isCustomer: data.isCustomer,
      isSupplier: data.isSupplier,
      domain: data.domain,
      billingAddress: data.billingAddress?.trim() || null,
      billingContact: {
        name: data.billingContact.name?.trim() || null,
        email: data.billingContact.email?.trim() || null,
        phone: data.billingContact.phone?.trim() || null,
      },
    });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={props.isOpen}
      onClose={close}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <SupportSnackbar
        isOpen={isAlertOpen}
        message="An error occurred while creating the company."
        onClose={createCustomerMutation.reset}
      />

      <DialogTitle>Add company</DialogTitle>

      <DialogContent dividers>
        <CreateCustomerFormFields
          mode={mode}
          control={control}
          watch={watch}
          setValue={setValue}
          errors={errors}
        />
      </DialogContent>

      <DialogActions>
        <Button size="small" onClick={close}>
          Cancel
        </Button>
        <Button
          size="small"
          type="submit"
          color="secondary"
          variant="contained"
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
